
import { useEffect, useRef, useState } from 'react';
import DataTable from '../../../components/data-table/DataTable';
import { getAccounts } from '../../../services/accountsService';
import SearchTable from '../../../components/search-table/SearchTable';
import { StyledCustomers } from './customers.styled';
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from '../../../helpers/helpers';
import { useSelector, useDispatch } from 'react-redux';
import { AccountStructure } from '../../../models/accounts-structure';
import { Account, AccountSchema, AccountSearch } from '../../../models/account';
import { RootState } from '../../../redux/store';
import { Skeleton } from '@mui/material';
import { CanceledError } from 'axios';
import HistoryIcon from '@mui/icons-material/History';
import ListItemIcon from '@mui/material/ListItemIcon';
import MovingIcon from '@mui/icons-material/Moving';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import EditIcon from '@mui/icons-material/Edit';
import { setCustomersSearch } from '../../../redux/features/browse';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
// import { addCustomDataNamesToAccount } from '../../../services/customDataService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from "@emotion/react";
import { useErrorBoundary } from "react-error-boundary";
import { handleApiErrors } from '../../../services/errorHandling';
import { editLastBread, getLastAvailableLink } from '../../../components/seperator/Seperator.utils';
import { useGetAccountStructureQuery } from '../../../redux/api/accounts-structure.api';

interface CustomersProps {
    searchFormData?: AccountSearch;
}

function Customers({ searchFormData }: CustomersProps) {


    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();

    // const customerStructure = useSelector((state: RootState) => state.structure.customers);
    const customersSearch = useSelector((state: RootState) => state.browse.customersSearch);

    const { data: customerStructure } = useGetAccountStructureQuery("לקוח-ספק");
    
        
    const [data, setData] = useState<Account[]>([]);
    const [searchStructure, setSearchStructure] = useState<AccountStructure[]>();
    const [tableStructure, setTableStructure] = useState<AccountStructure[]>();
    const [sortKey, setSortKey] = useState<string>('id');
    const [sortMethod, setSortMethod] = useState<string>('DESC');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const options = [
        {
            label: "עריכה",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><EditIcon fontSize="small" /></ListItemIcon>,
            href: "",
        },
        {
            label: "תנועות",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><MovingIcon fontSize="small" /></ListItemIcon>,
            href: "journal-entries"
        },
        {
            label: "הסטוריה",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><HistoryIcon fontSize="small" /></ListItemIcon>,
            href: "history"
        },
        {
            label: "מסמכים",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><ContactPageIcon fontSize="small" /></ListItemIcon>,
            href: "documents"
        },
    ];

    const accountSearchForm = useForm<AccountSearch>({
        defaultValues: {
            "account_type": "לקוח-ספק",
            ...customersSearch,
            ...searchFormData
        }
    });


    useEffect(() => {
        setSearchStructure(customerStructure?.filter(field => field.show_search))
        setTableStructure(customerStructure?.filter(field => field.show_browse))
    }, [customerStructure]);

    useEffect(() => {
        if (sortKey && sortMethod) {
            accountSearchForm.handleSubmit(onSearch)();
        }
    }, [sortKey, sortMethod]);


    useEffect(() => {
        const subscription = accountSearchForm.watch(() => {
            accountSearchForm.handleSubmit(onSearch)();
        })

        return () => subscription.unsubscribe();

    }, [accountSearchForm.watch]);



    function handleSortKeyChanged(value: string) {
        setSortKey(value);
    }

    function handleSortMethodChanged(value: string) {
        setSortMethod(value);
    }

    const onSearch: SubmitHandler<AccountSearch> = (data) => {
        dispatch(setCustomersSearch(data));
        // data = addCustomDataNamesToAccount(data);

        getData(data);
    }

    async function getData(data: AccountSearch) {

        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        setIsLoading(true);

        try {
            const responseData = await getAccounts(deleteEmptyFieldsFromObject(data), sortKey || 'id', "10", sortMethod || 'ASC', ["customData", "entries.document.documentSettings"], abortControllerRef.current.signal);
            const parseData = z.array(AccountSchema).parse(responseData);
            setData(parseData || []);
            setIsLoading(false);
        } catch (error: unknown) {
            if (error instanceof CanceledError) {
                return;
            }
            handleApiErrors(error, showBoundary);
        }

    }


    function rowChosen<T extends Account>(row: T) {

        if(location.state?.breadcrumbData){
            navigate(getLastAvailableLink(location.state.breadcrumbData), {
                replace: true,
                state: {
                    ...location.state,
                    document_account: {...row, id: undefined, account_id: row.id},
                    car: null,
                    breadcrumbData: location.state.breadcrumbData.slice(0, -1)
                }
            });
        }
    }

    function createState(){
        return {
            ...location.state,
        }
    }

    function createBreadcrumbData(state: any){

        const breadcrumbData = [
            {
                name: "כרטסות",
            },
            {
                name: "לקוחות/ספקים",
                link: "/customers",
            },
        ];

        return [...(editLastBread(state?.breadcrumbData, `/customers`, state) ?? breadcrumbData)];
    }

    function editSearchStructure() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate('/customers/structure/search', {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה חיפוש" }]
            }
        });
    }

    function editBrowseStructure() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate('/customers/structure/browse', {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה דפדוף" }]
            }
        });
    }

    function reset() {

        const values = setAllValuesToNull(accountSearchForm.getValues());

        for (const key in values) {

            if (values.hasOwnProperty(key)) {
                accountSearchForm.setValue(key as keyof AccountSearch, values[key as keyof AccountSearch]);
            }
        }

        accountSearchForm.setValue("account_type", "לקוח-ספק")
    }


    return (
        <StyledCustomers>
            {searchStructure ?
                <SearchTable
                    structure={searchStructure} useForm={accountSearchForm} setStructure={editSearchStructure} reset={reset} style={{ backgroundColor: "rgb(35, 48, 68)", padding: 5, marginButton: 20 }} /> :
                <Skeleton variant="rectangular" height={100} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
            {tableStructure ?
                <DataTable structure={tableStructure} sortKey={sortKey} setStructure={editBrowseStructure} setSortKey={handleSortKeyChanged} sortMethod={sortMethod} setSortMethod={handleSortMethodChanged} data={data} isLoading={isLoading} options={options} selectRow={location.state?.breadcrumbData ? true : false} onSelectRow={rowChosen} /> :
                <Skeleton variant="rectangular" height={150} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
        </StyledCustomers>
    );
}

export default Customers;